import Tooltip from "@reach/tooltip";
import React, { useMemo } from "react";
import styled from "styled-components";
import { InlineSeparator } from "./InlineSeparator";
import { Avatar } from "../avatar/Avatar";
import { AvatarPlaceholder } from "../avatar/AvatarPlaceholder";
import DateTimeFormat from "../date-time/DateTime";
import Contribute from "../icons/Contribute";
import VerifiedLabel from "../verified-label/VerifiedLabel";
import { Item } from "../../data/db/types";

const ContributorsListContainer = styled.div``;

const ContributorsListHeading = styled.h2`
  display: flex;
  align-items: center;
  color: var(--theme-color-primary);
  font-size: var(--default-font-size);
  line-height: var(--default-line-height);
  font-weight: var(--default-font-weight);
  padding: var(--spacing-large) 0;
  margin: 0;
  border-bottom: var(--card-stroke);

  [data-icon] {
    margin-right: var(--spacing-default);
  }
`;

const ContributorsListElement = styled.ul`
  list-item: none;
  padding: 0;
  margin: 0;
  border-bottom: var(--card-stroke);
`;
const ContributorsListItem = styled.li`
  display: flex;
  align-items: center;
  padding: var(--spacing-default) 0;
  margin: var(--spacing-default) 0;
  color: var(--text-color-muted);
  font-size: var(--text-level2-size);
  & > * + * {
    margin-left: var(--spacing-default);
  }
`;
const ContributorLabel = styled.span`
  color: var(--link-color);
`;
const ContributorWhen = styled.span``;

type Props = {
  contributors: Item["contributor"];
  contributionMeta: Item["_contributions"];
};
const ContributorsList: React.FC<Props> = ({
  contributors,
  contributionMeta
}) => {
  const contributorItems = useMemo(() => {
    const map = new Map(
      contributors?.map((c) => {
        return [c._id as string, c];
      })
    );
    return contributionMeta.map((meta) => {
      return {
        user: map.get(meta._author)!,
        numCommits: meta._commits,
        lastContribution: meta._lastContribution
      };
    });
  }, [contributors, contributionMeta]);

  return (
    <ContributorsListContainer>
      <ContributorsListHeading>
        <Contribute /> {contributorItems.length} contributors
      </ContributorsListHeading>
      <ContributorsListElement>
        {contributorItems.map(({ user, numCommits, lastContribution }) => {
          return (
            <ContributorsListItem key={user._id} data-num-commits={numCommits}>
              {user.image ? (
                <Avatar level="1" src={user.image} />
              ) : (
                <AvatarPlaceholder level="1" />
              )}
              {user._verifiedContributor ? (
                <Tooltip label="A 'Verified Contributor' is an official member of the IOG organisation.">
                  <VerifiedLabel>{user.name}</VerifiedLabel>
                </Tooltip>
              ) : (
                <ContributorLabel>{user?.name}</ContributorLabel>
              )}
              <InlineSeparator />
              <ContributorWhen>
                contributed <DateTimeFormat value={lastContribution} />
              </ContributorWhen>
            </ContributorsListItem>
          );
        })}
      </ContributorsListElement>
    </ContributorsListContainer>
  );
};

export default ContributorsList;
